import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'
import { school } from '../../tokens'
import { Crest } from '../display/crest'

/**
 * JPG Academy Vertical Navigation (Sidebar)
 *
 * Motion is built in and respects prefers-reduced-motion.
 */

interface VerticalNavContextValue {
	collapsed: boolean
	activeKey?: string
	onSelect?: (key: string) => void
	onToggleCollapse?: () => void
}
const VerticalNavContext = React.createContext<VerticalNavContextValue>({ collapsed: false })
const useVerticalNav = () => React.useContext(VerticalNavContext)

export interface VerticalNavProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onSelect'> {
	collapsed?: boolean
	onToggleCollapse?: () => void
	activeKey?: string
	onSelect?: (key: string) => void
	sticky?: boolean
}

export function VerticalNav({ collapsed = false, onToggleCollapse, activeKey, onSelect, sticky = true, className, children, ...props }: VerticalNavProps) {
	// Separate VerticalNavFooter children so they render outside the scrollable area (pinned at bottom)
	const mainChildren: React.ReactNode[] = []
	const footerChildren: React.ReactNode[] = []

	React.Children.forEach(children, child => {
		if (React.isValidElement(child) && (child.type as typeof VerticalNavFooter) === VerticalNavFooter) {
			footerChildren.push(child)
		} else {
			mainChildren.push(child)
		}
	})

	return (
		<VerticalNavContext.Provider value={{ collapsed, activeKey, onSelect, onToggleCollapse }}>
			<motion.aside
				className={cn('flex flex-col h-full bg-navy-900 text-cream-50', sticky && 'sticky top-0 z-sticky h-screen overflow-hidden', className)}
				initial={false}
				animate={{ width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)' }}
				transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
				role="navigation"
				aria-label="Main navigation"
				{...(props as any)}
			>
				<div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-warm min-h-0">{mainChildren}</div>
				{footerChildren.length > 0 && <div className="shrink-0">{footerChildren}</div>}
			</motion.aside>
		</VerticalNavContext.Provider>
	)
}

export function VerticalNavToggle({ className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
	const { collapsed, onToggleCollapse } = useVerticalNav()
	if (!onToggleCollapse) return null
	return (
		<motion.button
			type="button"
			onClick={onToggleCollapse}
			aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
			className={cn('flex items-center justify-center w-7 h-7 rounded-md text-cream/50 hover:text-cream hover:bg-navy-700 transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40', className)}
			whileHover={{ scale: 1.1 }}
			whileTap={{ scale: 0.9 }}
			{...(props as any)}
		>
			<motion.span animate={{ rotate: collapsed ? 180 : 0 }} transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}>
				<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
			</motion.span>
		</motion.button>
	)
}

export interface VerticalNavBrandProps extends React.HTMLAttributes<HTMLDivElement> {
	title?: string
	subtitle?: string
	showCrest?: boolean
	showSchoolName?: boolean
	showCollapseToggle?: boolean
}
export function VerticalNavBrand({ title, subtitle, showCrest = true, showSchoolName = true, showCollapseToggle = true, className, ...props }: VerticalNavBrandProps) {
	const { collapsed } = useVerticalNav()
	return (
		<div
			className={cn(
				'border-b border-navy-700/50 shrink-0',
				collapsed
					? 'flex flex-col items-center justify-center gap-2 px-2 py-3 min-h-[var(--header-height)]'
					: 'flex items-center gap-3 px-4 h-[var(--header-height)]',
				className,
			)}
			{...props}
		>
			{showCrest && <Crest size="sm" className="flex-shrink-0" />}
			<AnimatePresence>
				{!collapsed && (
					<motion.div className="flex flex-col min-w-0 flex-1 overflow-hidden" initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }} exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>
						<span className="font-display uppercase tracking-trajan-wide text-sm font-bold truncate leading-tight whitespace-nowrap">{title || school.shortName}</span>
						{showSchoolName && !title && <span className="font-body text-[10px] text-cream/40 truncate leading-tight whitespace-nowrap">{school.fullName}</span>}
						{subtitle && <span className="font-body text-[11px] text-cream/50 truncate leading-tight whitespace-nowrap">{subtitle}</span>}
					</motion.div>
				)}
			</AnimatePresence>
			{showCollapseToggle && <VerticalNavToggle className={collapsed ? 'border border-navy-700/70 bg-navy-800/70 text-cream/70 hover:bg-navy-700' : 'ml-auto'} />}
		</div>
	)
}

export interface VerticalNavGroupProps extends React.HTMLAttributes<HTMLDivElement> {
	label: string
	collapsible?: boolean
	defaultCollapsed?: boolean
}
export function VerticalNavGroup({ label, collapsible = false, defaultCollapsed = false, className, children, ...props }: VerticalNavGroupProps) {
	const { collapsed } = useVerticalNav()
	const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed)
	const showItems = collapsed || !isCollapsed
	return (
		<div className={cn('flex flex-col mt-4 first:mt-0', className)} {...props}>
			<button type="button" onClick={collapsible && !collapsed ? () => setIsCollapsed(c => !c) : undefined} className={cn('flex items-center gap-2 px-4 h-7 w-full transition-colors duration-fast cursor-default', collapsible && !collapsed && 'cursor-pointer hover:text-cream')}>
				<motion.span className="font-display uppercase font-bold tracking-trajan-tight whitespace-nowrap text-[10px] text-cream/40 overflow-hidden" animate={{ width: collapsed ? 0 : 'auto', opacity: collapsed ? 0 : 1 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>{label}</motion.span>
				{collapsed && <span className="w-4 h-px bg-cream/20" aria-hidden="true" />}
				{collapsible && !collapsed && <motion.span className="ml-auto text-cream/30" animate={{ rotate: !showItems ? -90 : 0 }} transition={{ duration: 0.2 }}><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg></motion.span>}
			</button>
			<AnimatePresence initial={false}>
				{showItems && <motion.div className="flex flex-col mt-0.5" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>{children}</motion.div>}
			</AnimatePresence>
		</div>
	)
}

export interface VerticalNavItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	itemKey: string
	icon?: React.ReactNode
	badge?: React.ReactNode
	indent?: boolean
}
export const VerticalNavItem = React.forwardRef<HTMLButtonElement, VerticalNavItemProps>(({ itemKey, icon, badge, indent = false, className, children, ...props }, ref) => {
	const { collapsed, activeKey, onSelect } = useVerticalNav()
	const isActive = itemKey === activeKey
	return (
		<motion.button
			ref={ref}
			type="button"
			onClick={() => onSelect?.(itemKey)}
			aria-current={isActive ? 'page' : undefined}
			title={collapsed ? String(children) : undefined}
			className={cn('relative flex items-center gap-3 min-h-[36px]', collapsed ? 'justify-center px-0 mx-2' : indent ? 'pl-10 pr-4' : 'pl-4 pr-3', 'rounded-md mx-2 font-ui text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 group cursor-pointer disabled:opacity-40 disabled:pointer-events-none', isActive ? 'bg-navy-700/60 text-cream' : 'text-cream/60 hover:text-cream hover:bg-navy-700/30', className)}
			whileHover={!props.disabled ? { x: 2 } : undefined}
			transition={{ type: 'spring', stiffness: 400, damping: 30 }}
			{...(props as any)}
		>
			{isActive && <motion.span className="absolute left-0 top-1 bottom-1 w-[3px] bg-gold rounded-full" layoutId="vnav-active-indicator" transition={{ type: 'spring', stiffness: 300, damping: 30 }} aria-hidden="true" />}
			{icon && <span className={cn('flex-shrink-0 transition-colors duration-fast', isActive ? 'text-gold' : 'text-cream/40 group-hover:text-cream/70')}>{icon}</span>}
			<AnimatePresence>{!collapsed && <motion.span className="truncate flex-1 text-left" initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }} exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.15 }}>{children}</motion.span>}</AnimatePresence>
			<AnimatePresence>{!collapsed && badge && <motion.span className="flex-shrink-0" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.15 }}>{badge}</motion.span>}</AnimatePresence>
		</motion.button>
	)
})
VerticalNavItem.displayName = 'VerticalNavItem'

export function VerticalNavDivider({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	const { collapsed } = useVerticalNav()
	if (collapsed) return <div className={cn('mx-4 my-2 h-px bg-cream/10', className)} aria-hidden="true" {...props} />
	return <div className={cn('mx-4 my-3', className)} aria-hidden="true" {...props}><div className="h-px bg-gradient-to-r from-transparent via-gold/25 to-transparent" /></div>
}

export function VerticalNavFooter({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	const { collapsed } = useVerticalNav()
	return <div className={cn('flex items-center gap-3 px-4 py-3 border-t border-navy-700/50 shrink-0', className)} {...props}>{children}{collapsed && <span className="sr-only">{typeof children === 'string' ? children : 'User menu'}</span>}</div>
}
